import * as THREE from 'three';

/**
 * Find an animation name from a list by matching candidates or required keywords
 */
export function findAnimationName(
  animationNames: string[],
  candidates: string[],
  requiredKeywords?: string[]
): string | undefined {
  // First, try exact matches
  for (const candidate of candidates) {
    if (animationNames.includes(candidate)) {
      return candidate;
    }
  }

  // If no exact match and we have required keywords, search by keyword matching
  if (requiredKeywords && requiredKeywords.length > 0) {
    for (const animName of animationNames) {
      const lowerName = animName.toLowerCase();
      const allKeywordsMatch = requiredKeywords.every((keyword) => lowerName.includes(keyword));
      if (allKeywordsMatch) {
        return animName;
      }
    }
  }

  return undefined;
}

/**
 * Choose the best camera from a list by analyzing animation tracks
 * Prefers cameras with more animation data
 */
export function choosePreferredAnimatedCamera(
  cameras: THREE.Camera[],
  animations: THREE.AnimationClip[]
): THREE.Camera | null {
  if (!cameras || cameras.length === 0) {
    return null;
  }

  if (cameras.length === 1) {
    return cameras[0];
  }

  // Count animation tracks targeting each camera
  const cameraTrackCounts = new Map<string, number>();

  for (const clip of animations) {
    for (const track of clip.tracks) {
      // Tracks follow pattern: "ObjectName.property" or "ObjectName.quaternion"
      const objectName = track.name.split('.')[0];

      for (const camera of cameras) {
        if (camera.name === objectName) {
          const count = cameraTrackCounts.get(camera.name) ?? 0;
          cameraTrackCounts.set(camera.name, count + 1);
        }
      }
    }
  }

  // Find camera with most animation tracks
  let bestCamera: THREE.Camera = cameras[0];
  let bestScore = cameraTrackCounts.get(cameras[0].name) ?? 0;

  for (const camera of cameras) {
    const score = cameraTrackCounts.get(camera.name) ?? 0;
    if (score > bestScore) {
      bestScore = score;
      bestCamera = camera;
    }
  }

  return bestCamera ?? cameras[0];
}
